import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { coinAPI, rankAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface RankInfo {
  name: string;
  displayName: string;
  color: string;
  weight: number;
}

interface Transaction {
  id: number;
  transactionUuid: string;
  type: string;
  amount: number;
  oldBalance: number;
  newBalance: number;
  reason: string | null;
  timestamp: Date;
}

interface PlayerContextType {
  coinBalance: number;
  rank: RankInfo | null;
  transactions: Transaction[];
  isLoading: boolean;
  refreshBalance: () => Promise<void>;
  refreshRank: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const usePlayer = (): PlayerContextType => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

interface PlayerProviderProps {
  children: ReactNode;
}

export const PlayerProvider: React.FC<PlayerProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [coinBalance, setCoinBalance] = useState<number>(0);
  const [rank, setRank] = useState<RankInfo | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refreshBalance = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await coinAPI.getBalance();
      setCoinBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Failed to fetch coin balance:', error);
    }
  }, [isAuthenticated]);

  const refreshRank = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await rankAPI.getMyRank();
      setRank(response.data.rank || null);
    } catch (error) {
      console.error('Failed to fetch rank:', error);
    }
  }, [isAuthenticated]);

  const refreshTransactions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await coinAPI.getHistory(1, 10);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, [isAuthenticated]);

  const refreshAll = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      await Promise.all([refreshBalance(), refreshRank(), refreshTransactions()]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, refreshBalance, refreshRank, refreshTransactions]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshAll();
    } else {
      setCoinBalance(0);
      setRank(null);
      setTransactions([]);
    }
  }, [isAuthenticated, refreshAll]);

  return (
    <PlayerContext.Provider
      value={{
        coinBalance,
        rank,
        transactions,
        isLoading,
        refreshBalance,
        refreshRank,
        refreshTransactions,
        refreshAll,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export default PlayerContext;
